import type { FC, ReactNode } from "react";

import { getFamilyAccent, resolveModel } from "../blackboard/modelRegistry";

/*
 * Arti's public pages share the dashboard's visual language: square
 * slate panels on an ink grid, bracketed monospace labels and emerald
 * accents. Keep decoration to that vocabulary (no gradients or glows).
 */

/* ------------------------------------------------------------------ */
/*  Buttons                                                            */
/* ------------------------------------------------------------------ */

export const PRIMARY_BUTTON_CLASS_NAME =
  "group inline-flex cursor-pointer items-center justify-center gap-2 bg-emerald-500 px-5 py-3 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-950 transition hover:bg-emerald-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50";

export const SECONDARY_BUTTON_CLASS_NAME =
  "group inline-flex cursor-pointer items-center justify-center gap-2 border-2 border-slate-700 px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-300 transition hover:border-emerald-500/60 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50";

/** Small monospace caption: spec labels, kickers, column headings. */
export const MONO_LABEL_CLASS_NAME =
  "font-mono text-[11px] font-bold uppercase tracking-[0.18em]";

export const ArrowRightIcon: FC = () => (
  <svg
    className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4.167 10h11.666m0 0-4.166-4.167M15.833 10l-4.166 4.167"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="square"
    />
  </svg>
);

export const CheckIcon: FC<{ className?: string }> = ({
  className = "h-4 w-4",
}) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="m5 10.5 3.2 3.2L15 6.8"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="square"
    />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Labels and chips                                                   */
/* ------------------------------------------------------------------ */

/** Bracketed monospace label, e.g. `[ SKILL MESH ]`. */
export const Eyebrow: FC<{ children: ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.22em] text-emerald-400">
    <span className="text-slate-600" aria-hidden="true">
      [
    </span>
    {children}
    <span className="text-slate-600" aria-hidden="true">
      ]
    </span>
  </span>
);

type ChipTone = "emerald" | "slate";

const CHIP_TONE_CLASS: Record<ChipTone, { box: string; square: string }> = {
  emerald: {
    box: "border-emerald-500/45 bg-emerald-500/10 text-emerald-200",
    square: "bg-emerald-400",
  },
  slate: {
    box: "border-slate-600 bg-slate-800 text-slate-300",
    square: "bg-slate-400",
  },
};

/** Square status chip, e.g. `■ LIVE`. */
export const Chip: FC<{
  children: ReactNode;
  square?: boolean;
  tone?: ChipTone;
}> = ({ children, square = false, tone = "emerald" }) => (
  <span
    className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.16em] ${CHIP_TONE_CLASS[tone].box}`}
  >
    {square ? (
      <span
        className={`h-1.5 w-1.5 ${CHIP_TONE_CLASS[tone].square}`}
        aria-hidden="true"
      />
    ) : null}
    {children}
  </span>
);

export const LiveDot: FC<{ label?: string }> = ({ label = "Live" }) => (
  <Chip square>{label}</Chip>
);

/* ------------------------------------------------------------------ */
/*  Headings                                                           */
/* ------------------------------------------------------------------ */

/** Second line of a two-part heading, set lighter. */
export const SubduedText: FC<{ children: ReactNode }> = ({ children }) => (
  <span className="text-slate-400">{children}</span>
);

export const SectionHeading: FC<{
  align?: "center" | "left";
  body?: ReactNode;
  eyebrow: string;
  title: ReactNode;
}> = ({ align = "left", body, eyebrow, title }) => (
  <div
    className={
      align === "center" ? "mx-auto max-w-3xl text-center" : "max-w-3xl"
    }
  >
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className="mt-4 text-balance font-display text-3xl font-bold normal-case leading-[1.12] !tracking-tight text-white sm:text-4xl">
      {title}
    </h2>
    {body ? (
      <p className="mt-4 text-base leading-7 text-slate-400 sm:text-lg sm:leading-8">
        {body}
      </p>
    ) : null}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Layout                                                             */
/* ------------------------------------------------------------------ */

export const LandingSection: FC<{
  band?: boolean;
  children: ReactNode;
  id?: string;
}> = ({ band = false, children, id }) => (
  <section
    id={id}
    className={`relative scroll-mt-24 px-4 py-16 sm:px-6 lg:py-24 ${band ? "border-y-2 border-slate-800/80 bg-slate-950/30" : ""}`}
  >
    <div className="mx-auto max-w-7xl">{children}</div>
  </section>
);

/** Emerald corner ticks, the signature on a highlighted panel. */
export const Corners: FC = () => (
  <>
    <span
      className="pointer-events-none absolute -left-0.5 -top-0.5 h-3 w-3 border-l-2 border-t-2 border-emerald-400"
      aria-hidden="true"
    />
    <span
      className="pointer-events-none absolute -right-0.5 -top-0.5 h-3 w-3 border-r-2 border-t-2 border-emerald-400"
      aria-hidden="true"
    />
    <span
      className="pointer-events-none absolute -bottom-0.5 -left-0.5 h-3 w-3 border-b-2 border-l-2 border-emerald-400"
      aria-hidden="true"
    />
    <span
      className="pointer-events-none absolute -bottom-0.5 -right-0.5 h-3 w-3 border-b-2 border-r-2 border-emerald-400"
      aria-hidden="true"
    />
  </>
);

/** Marketing surface: a square slate panel. */
export const Panel: FC<{
  as?: "article" | "div";
  children: ReactNode;
  className?: string;
  corners?: boolean;
}> = ({ as: Tag = "div", children, className = "", corners = false }) => (
  <Tag
    className={`relative border-2 border-slate-800 bg-[#111a2e]/90 ${className}`}
  >
    {corners ? <Corners /> : null}
    {children}
  </Tag>
);

/**
 * Product surface, drawn like a dashboard panel: a bracketed monospace
 * title bar over the content.
 */
export const WindowFrame: FC<{
  children: ReactNode;
  className?: string;
  corners?: boolean;
  footer?: ReactNode;
  status?: ReactNode;
  title: string;
}> = ({ children, className = "", corners = false, footer, status, title }) => (
  <div
    className={`relative border-2 border-slate-700 bg-[#111a2e]/95 shadow-[0_18px_50px_rgba(0,0,0,0.45)] ${className}`}
  >
    {corners ? <Corners /> : null}
    <div className="flex h-11 items-center gap-3 border-b-2 border-slate-700 bg-slate-900/90 px-4">
      <span className="min-w-0 flex-1 truncate font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-slate-200">
        <span className="text-slate-600">[</span> {title}{" "}
        <span className="text-slate-600">]</span>
      </span>
      {status}
    </div>
    {children}
    {footer ? (
      <div className="border-t-2 border-slate-700 bg-slate-900/60 px-4 py-2.5">
        {footer}
      </div>
    ) : null}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Model badge                                                        */
/* ------------------------------------------------------------------ */

export const ModelBadge: FC<{ modelRef: string; size?: "md" | "sm" }> = ({
  modelRef,
  size = "md",
}) => {
  const { family, label } = resolveModel(modelRef);
  const accent = getFamilyAccent(family);
  const sizeClassName =
    size === "sm" ? "px-1.5 py-px text-[8px]" : "px-1.5 py-px text-[9px]";
  return (
    <span
      className={`inline-flex flex-shrink-0 border font-mono font-bold uppercase tracking-widest ${sizeClassName} ${accent.border} ${accent.text}`}
    >
      {label}
    </span>
  );
};
